import { forwardRef, useImperativeHandle, useState, useRef, useEffect } from 'react';
import { format, parse } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { DatePicker } from '@shared/components/ui/date-picker';
import { Input } from '@shared/components/ui/input';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/pro-light-svg-icons';
import { Incident, IncidentActionsResponse, IncidentAction } from '../../models/Incident';
import { useSolveIncident } from '../../hooks';
import { useToast } from '@shared/hooks/use-toast';

export interface SolveIncidentModalRef {
  open: (actionId?: number) => void;
  close: () => void;
}

interface SolveIncidentModalProps {
  incident: Incident;
  actions?: IncidentActionsResponse;
  onSuccess?: () => void;
}

export const SolveIncidentModal = forwardRef<SolveIncidentModalRef, SolveIncidentModalProps>(
  ({ incident, actions, onSuccess }, ref) => {
    const { t } = useTranslation('incidentsModule');
    const { toast } = useToast();
    const modalRef = useRef<ModalCenterRef>(null);
    const solveMutation = useSolveIncident();

    const [actionId, setActionId] = useState<string>('');
    const [formValues, setFormValues] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    type PayloadField = NonNullable<NonNullable<IncidentAction['payload_response']>['payload_fields']>[number];

    const selectedAction = actions?.items.find((a) => a.id.toString() === actionId);
    const payloadFields = selectedAction?.payload_response?.payload_fields ?? [];
    const metaFields = selectedAction?.payload_response?.meta_fields ?? [];
    const formFields = [...payloadFields, ...metaFields];

    useImperativeHandle(ref, () => ({
      open: (actionId?: number) => {
        modalRef.current?.openModal();
        // Si se pasa un actionId, usarlo directamente; si no, dejar vacío
        setActionId(actionId ? actionId.toString() : '');
        setFormValues({});
        setFieldErrors({});
      },
      close: () => {
        modalRef.current?.closeModal();
      },
    }));

    useEffect(() => {
      const currentPayloadFields = selectedAction?.payload_response?.payload_fields ?? [];
      const currentMetaFields = selectedAction?.payload_response?.meta_fields ?? [];
      const currentFormFields = [...currentPayloadFields, ...currentMetaFields];

      if (currentFormFields.length) {
        const defaults = currentFormFields.reduce<Record<string, string>>((acc, field) => {
          acc[field.payload_name] = '';
          return acc;
        }, {});
        setFormValues(defaults);
      } else {
        setFormValues({});
      }

      setFieldErrors({});
    }, [selectedAction]);

    const parseDateValue = (value?: string) => {
      if (!value) return undefined;
      const parsed = parse(value, 'yyyy-MM-dd', new Date());
      return Number.isNaN(parsed.getTime()) ? undefined : parsed;
    };

    const formatDateValue = (value?: Date) => (value ? format(value, 'yyyy-MM-dd') : '');

    const renderField = (field: PayloadField, className?: string) => {
      const value = formValues[field.payload_name] ?? '';
      const commonLabel = <label className="text-sm font-medium text-title mb-1 block">{field.label}</label>;

      if (field.type === 'select') {
        return (
          <div key={field.payload_name} className={`space-y-1 ${className || ''}`}>
            {commonLabel}
            <Select
              value={value || undefined}
              onValueChange={(newValue) => setFormValues((prev) => ({ ...prev, [field.payload_name]: newValue }))}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={field.placeholder || t('detail.modals.solve.select_action')} />
              </SelectTrigger>
              <SelectContent>
                {field.options?.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      }

      if (field.type === 'date') {
        return (
          <div key={field.payload_name} className={`space-y-1 ${className || ''}`}>
            {commonLabel}
            <DatePicker
              date={parseDateValue(value)}
              onDateChange={(newDate) =>
                setFormValues((prev) => ({
                  ...prev,
                  [field.payload_name]: formatDateValue(newDate),
                }))
              }
              placeholder={field.placeholder}
              data-testid={`payload-date-${field.payload_name}`}
            />
          </div>
        );
      }

      if (field.type === 'number') {
        const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const inputValue = e.target.value;

          // Actualizar el valor (el navegador manejará la validación con type="number")
          setFormValues((prev) => ({ ...prev, [field.payload_name]: inputValue }));

          // Validar min y max si existen para mostrar mensajes de error personalizados
          let error = '';
          if (inputValue !== '') {
            const numValue = parseFloat(inputValue);
            if (!isNaN(numValue)) {
              if (field.min !== undefined && numValue < field.min) {
                error = `El valor mínimo es ${field.min}`;
              } else if (field.max !== undefined && numValue > field.max) {
                error = `El valor máximo es ${field.max}`;
              }
            }
          }

          setFieldErrors((prev) => ({
            ...prev,
            [field.payload_name]: error,
          }));
        };

        return (
          <div key={field.payload_name} className={`space-y-1 ${className || ''}`}>
            {commonLabel}
            <Input
              type="number"
              min={field.min}
              max={field.max}
              value={value}
              onChange={handleNumberChange}
              placeholder={field.placeholder}
              className={`w-full ${fieldErrors[field.payload_name] ? 'border-error' : ''}`}
              data-testid={`payload-number-${field.payload_name}`}
            />
            {(fieldErrors[field.payload_name] || field.min !== undefined || field.max !== undefined) && (
              <div className="flex items-center gap-1 flex-wrap">
                {fieldErrors[field.payload_name] && (
                  <span className="text-xs text-error">{fieldErrors[field.payload_name]}</span>
                )}
                {(field.min !== undefined || field.max !== undefined) && (
                  <span className="text-xs text-complementary-text">
                    {field.min !== undefined && field.max !== undefined
                      ? `Rango: ${field.min} - ${field.max}`
                      : field.min !== undefined
                        ? `Mínimo: ${field.min}`
                        : `Máximo: ${field.max}`}
                  </span>
                )}
              </div>
            )}
          </div>
        );
      }

      if (field.type === 'textarea') {
        return (
          <div key={field.payload_name} className={`space-y-1 ${className || ''}`}>
            {commonLabel}
            <textarea
              value={value}
              onChange={(e) => setFormValues((prev) => ({ ...prev, [field.payload_name]: e.target.value }))}
              placeholder={field.placeholder}
              className="flex min-h-[80px] w-full rounded-md border border-card-border bg-card-bg px-3 py-2 text-sm text-text shadow-sm transition-colors placeholder:text-default-text focus-visible:outline-none focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50 resize-y"
              rows={4}
              data-testid={`payload-textarea-${field.payload_name}`}
            />
          </div>
        );
      }

      // Tipo 'text' por defecto
      return (
        <div key={field.payload_name} className={`space-y-1 ${className || ''}`}>
          {commonLabel}
          <Input
            type="text"
            value={value}
            onChange={(e) => setFormValues((prev) => ({ ...prev, [field.payload_name]: e.target.value }))}
            placeholder={field.placeholder}
            className="w-full"
            data-testid={`payload-text-${field.payload_name}`}
          />
        </div>
      );
    };

    const handleSubmit = async () => {
      if (!actionId) {
        toast({
          title: t('toasts.error.title'),
          description: t('detail.modals.solve.select_action'),
          variant: 'destructive',
        });
        return;
      }

      // Validar campos number antes de enviar
      const hasErrors = Object.values(fieldErrors).some((error) => error !== '');
      if (hasErrors) {
        toast({
          title: t('toasts.error.title'),
          description: 'Por favor corrige los errores en el formulario',
          variant: 'destructive',
        });
        return;
      }

      // Validar campos requeridos de payload/meta antes de enviar.
      const missingField = formFields.find(
        (field) => field.required && !(formValues[field.payload_name] || '').toString().trim(),
      );
      if (missingField) {
        toast({
          title: t('toasts.error.title'),
          description: `${missingField.label}: campo obligatorio`,
          variant: 'destructive',
        });
        return;
      }

      const comentarioValue = formValues.comentario ?? '';
      if (comentarioValue.length > 1000) {
        toast({
          title: t('toasts.error.title'),
          description: 'El comentario no puede superar 1000 caracteres',
          variant: 'destructive',
        });
        return;
      }

      // Validar campos number con min/max.
      for (const field of formFields) {
        if (field.type === 'number') {
          const value = formValues[field.payload_name];
          if (value && value !== '') {
            const numValue = parseInt(value, 10);
            if (field.min !== undefined && numValue < field.min) {
              toast({
                title: t('toasts.error.title'),
                description: `${field.label}: El valor mínimo es ${field.min}`,
                variant: 'destructive',
              });
              return;
            }
            if (field.max !== undefined && numValue > field.max) {
              toast({
                title: t('toasts.error.title'),
                description: `${field.label}: El valor máximo es ${field.max}`,
                variant: 'destructive',
              });
              return;
            }
          }
        }
      }

      setIsSubmitting(true);

      try {
        const payloadFieldNames = new Set(payloadFields.map((field) => field.payload_name));
        const metaFieldNames = new Set(metaFields.map((field) => field.payload_name));

        const payloadToSend = Object.entries(formValues).reduce<Record<string, string>>((acc, [key, value]) => {
          if (payloadFieldNames.has(key) && value !== '') {
            acc[key] = value;
          }
          return acc;
        }, {});

        const metaToSend = Object.entries(formValues).reduce<Record<string, string>>((acc, [key, value]) => {
          if (metaFieldNames.has(key) && value !== '') {
            acc[key] = value;
          }
          return acc;
        }, {});

        const { comentario, ...extraMetaFields } = metaToSend;
        const idempotencyKey = crypto.randomUUID();
        const response = await solveMutation.mutateAsync({
          id: incident.id,
          payload: {
            action_id: parseInt(actionId, 10),
            payload: Object.keys(payloadToSend).length ? payloadToSend : null,
            ...(comentario ? { comentario } : {}),
            ...extraMetaFields,
          },
          idempotencyKey,
        });

        if (response.success) {
          toast({
            title: t('toasts.success.title'),
            description: response.message || t('detail.modals.solve.success'),
            variant: 'default',
          });

          modalRef.current?.closeModal();
          onSuccess?.();
        } else {
          toast({
            title: t('toasts.error.title'),
            description: response.message || t('toasts.error.unexpected'),
            variant: 'destructive',
          });
        }
      } catch (error: any) {
        console.log('🔍 SolveIncidentModal - Error:', error);
        toast({
          title: t('toasts.error.title'),
          description: error?.message || t('toasts.error.unexpected'),
          variant: 'destructive',
        });
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <ModalCenter
        ref={modalRef}
        title={t('detail.modals.solve.title')}
        type="default"
        width="lg"
        classNameBody="max-h-[80vh] overflow-y-auto"
      >
        <div className="space-y-4">
          {/* Mostrar información de la acción seleccionada si ya viene pre-seleccionada */}
          {actionId && (
            <div className="flex items-center gap-2 text-text text-base font-medium">
              <FontAwesomeIcon icon={faPlay} className="text-text w-4 h-4" />
              {actions?.items.find((a) => a.id.toString() === actionId)?.name || actionId}
            </div>
          )}

          {/* Campos dinámicos del formulario */}
          <div className="space-y-2">
            {payloadFields.length || metaFields.length ? (
              <div className="space-y-3">
                {payloadFields.length > 0 && (
                  <div className={payloadFields.length > 1 ? 'grid grid-cols-1 sm:grid-cols-2 gap-3' : 'space-y-3'}>
                    {payloadFields.map((field, idx) => {
                      const useTwoColumns = payloadFields.length > 1;
                      const isLastSingle =
                        useTwoColumns && payloadFields.length % 2 === 1 && idx === payloadFields.length - 1;
                      const spanClass = isLastSingle ? 'sm:col-span-2' : '';
                      return renderField(field, spanClass);
                    })}
                  </div>
                )}

                {payloadFields.length > 0 && metaFields.length > 0 && <div className="border-t border-lines my-2" />}

                {metaFields.length > 0 && (
                  <div className={metaFields.length > 1 ? 'grid grid-cols-1 sm:grid-cols-2 gap-3' : 'space-y-3'}>
                    {metaFields.map((field, idx) => {
                      const useTwoColumns = metaFields.length > 1;
                      const isLastSingle =
                        useTwoColumns && metaFields.length % 2 === 1 && idx === metaFields.length - 1;
                      const spanClass = isLastSingle ? 'sm:col-span-2' : '';
                      return renderField(field, spanClass);
                    })}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-complementary-text">Esta acción no requiere datos adicionales.</p>
            )}
          </div>

          {/* Footer con acciones */}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => modalRef.current?.closeModal()}
              disabled={isSubmitting}
              data-testid="solve-cancel-button"
              size="lg"
            >
              {t('detail.modals.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={isSubmitting || !actionId}
              data-testid="solve-submit-button"
              size="lg"
            >
              {isSubmitting ? t('detail.modals.solving') : t('detail.modals.solve.submit')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

SolveIncidentModal.displayName = 'SolveIncidentModal';
